import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { roundToPrecision } from '@shared/utils';
import { OptionsSeatControlNames } from '@shared/components/delivery-cargo/components/delivery-cargo-places/delivery-cargo-places.enums';
import { DeliveryPalletsControlNames } from '@shared/components/delivery-pallets/delivery-pallets.enums';

// When the total announced cost (Cost) is raised to equate it to backDelivery/afterpayment sum,
// Cargo/Pallet shipments with several places keep their own per-place cost (OptionsSeat[].cost /
// RowCost[]) that sums up into Cost, so that breakdown must be scaled up by the same coefficient,
// or it would silently overwrite the raised total on its next place-cost sync.
export function redistributePlacesCost(
  stepDeliveryForm: UntypedFormGroup | null | undefined,
  previousCost: string | number,
  newCost: string | number,
): void {
  if (!stepDeliveryForm) {
    return;
  }

  const cargoType = stepDeliveryForm.get('CargoType')?.value;

  if (cargoType === 'Cargo' && !stepDeliveryForm.get('paramsCommonSeats')?.value) {
    const optionsSeat = stepDeliveryForm.get('OptionsSeat') as UntypedFormArray;
    redistributeCostControls(
      optionsSeat?.controls.map((control) => control.get(OptionsSeatControlNames.Cost)),
      previousCost,
      newCost,
    );
  }

  if (cargoType === 'Pallet') {
    const rowCost = stepDeliveryForm.get(DeliveryPalletsControlNames.RowCost) as UntypedFormArray;
    redistributeCostControls(rowCost?.controls, previousCost, newCost);
  }
}

// Each place's own cost is capped individually (PLACE_COST_INPUT_CONFIG/ROW_COST_INPUT_CONFIG.maxValue),
// but that doesn't stop several places from summing above the shipment's overall max announced cost,
// so this validator re-checks the running total on every place-cost control whenever it changes.
export function totalCostSumValidator(getTotal: () => number, maxValue: number): ValidatorFn {
  return (): ValidationErrors | null => (getTotal() > maxValue ? { totalCostExceeded: true } : null);
}

export function parseCostValue(value: string | number | null | undefined): number {
  return Number(String(value ?? '').replace(',', '.')) || 0;
}

function redistributeCostControls(
  controls: (AbstractControl | null)[] | undefined,
  previousCost: string | number,
  newCost: string | number,
): void {
  if (!controls || controls.length < 2) {
    return;
  }

  const newCostNumber = Number(newCost) || 0;
  const previousTotal = Number(previousCost) || 0;
  const ratio = previousTotal ? newCostNumber / previousTotal : null;

  controls.forEach((control) => {
    if (!control) {
      return;
    }
    const nextValue = ratio ? (Number(control.value) || 0) * ratio : newCostNumber / controls.length;
    control.setValue(nextValue ? roundToPrecision(nextValue, 2) : '', { emitEvent: false });
  });
}
